export const slugifyListKey = name =>
    (name || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')

export const normalizeList = raw => {
    if (!raw) return null
    return {
        id: raw.Id ?? raw.id,
        companyId: raw.CompanyId ?? raw.companyId,
        listName: raw.ListName ?? raw.listName ?? '',
        listKey: raw.ListKey ?? raw.listKey ?? '',
        isActive: raw.IsActive ?? raw.isActive ?? true,
        itemCount: raw.ItemCount ?? raw.itemCount,
    }
}

export const normalizeItem = (raw, index = 0) => {
    if (!raw) return null
    const resolvedId =
        raw.Id ?? raw.id ?? raw.ItemId ?? raw.itemId ?? null
    return {
        id:
            resolvedId ??
            `local-item-${index}-${raw.DisplayOrder ?? raw.displayOrder ?? 0}`,
        displayText: raw.DisplayText ?? raw.displayText ?? '',
        displayOrder: raw.DisplayOrder ?? raw.displayOrder ?? 0,
        score: raw.Score ?? raw.score ?? 0,
        backColorCode: raw.BackColorCode ?? raw.backColorCode ?? '',
        foreColorCode: raw.ForeColorCode ?? raw.foreColorCode ?? '',
        helpText: raw.HelpText ?? raw.helpText ?? '',
        isScoreFromQuestion:
            raw.IsScoreFromQuestion ?? raw.isScoreFromQuestion ?? false,
        isExcludedFromScoring:
            raw.IsExcludedFromScoring ?? raw.isExcludedFromScoring ?? false,
        allowManualScore: raw.AllowManualScore ?? raw.allowManualScore ?? false,
        maxManualScore: raw.MaxManualScore ?? raw.maxManualScore ?? null,
        isActive: raw.IsActive ?? raw.isActive ?? true,
    }
}

export const normalizeLists = data => {
    const rows = Array.isArray(data) ? data : data?.Items ?? data?.items ?? []
    return rows.map(normalizeList).filter(Boolean)
}

export const normalizeItems = data => {
    const rows = Array.isArray(data) ? data : data?.Items ?? data?.items ?? []
    return rows.map((raw, index) => normalizeItem(raw, index)).filter(Boolean)
}

export const getApiErrorMessage = err => {
    if (!err) return 'Something went wrong'
    if (err.status === 404) {
        return 'Master Answer Lists are not available for this company yet. Confirm the feature flag is enabled on QA.'
    }
    if (err.status === 403) {
        return 'You do not have permission to manage Master Answer Lists for this company.'
    }
    const e = err.error ?? err
    if (typeof e === 'string') return e
    if (e?.Message) return e.Message
    if (e?.message) return e.message
    if (Array.isArray(e) && e.length) {
        return e.map(x => x?.Message || x?.message || String(x)).join(' ')
    }
    if (err.status === 400) {
        return 'Invalid request. Display text cannot be empty, and each field must meet API rules.'
    }
    return 'Something went wrong'
}

export const itemToApiPayload = (fields = {}) => {
    const payload = {}
    if ('displayText' in fields) {
        const text = (fields.displayText ?? '').trim()
        if (!text) return null
        payload.DisplayText = text
    }
    if ('displayOrder' in fields) payload.DisplayOrder = fields.displayOrder
    if ('score' in fields) payload.Score = fields.score
    if ('backColorCode' in fields) payload.BackColorCode = fields.backColorCode || null
    if ('foreColorCode' in fields) payload.ForeColorCode = fields.foreColorCode || null
    if ('helpText' in fields) payload.HelpText = fields.helpText || null
    if ('isScoreFromQuestion' in fields) {
        payload.IsScoreFromQuestion = fields.isScoreFromQuestion
    }
    if ('isExcludedFromScoring' in fields) {
        payload.IsExcludedFromScoring = fields.isExcludedFromScoring
    }
    if ('allowManualScore' in fields) {
        payload.AllowManualScore = fields.allowManualScore
    }
    if ('maxManualScore' in fields) {
        payload.MaxManualScore = fields.maxManualScore
    }
    if ('isActive' in fields) payload.IsActive = fields.isActive
    return payload
}

/** Default label for POST create — API returns 400 when DisplayText is empty. */
export const NEW_ITEM_DEFAULT_LABEL = 'New response'

export const newItemTemplate = (displayOrder, displayText = NEW_ITEM_DEFAULT_LABEL) => ({
    DisplayText: (displayText || NEW_ITEM_DEFAULT_LABEL).trim() || NEW_ITEM_DEFAULT_LABEL,
    Score: 0,
    DisplayOrder: displayOrder,
    BackColorCode: '',
    ForeColorCode: '',
    HelpText: '',
    IsScoreFromQuestion: false,
    IsExcludedFromScoring: false,
    AllowManualScore: false,
})

/** Parse a simple CSV (DisplayText, Score, …) into bulk item payloads. */
export const parseCsvToItems = text => {
    const lines = text
        .split(/\r?\n/)
        .map(l => l.trim())
        .filter(Boolean)
    if (!lines.length) return []

    const firstCells = lines[0].split(',').map(c => c.trim().toLowerCase())
    const hasHeader =
        firstCells.includes('displaytext') || firstCells.includes('display text')
    const dataLines = hasHeader ? lines.slice(1) : lines

    return dataLines.map((line, idx) => {
        const cells = line.split(',').map(c => c.trim().replace(/^"|"$/g, ''))
        const displayText = cells[0] || ''
        const score = cells[1] !== undefined && cells[1] !== '' ? Number(cells[1]) : 0
        return {
            ...newItemTemplate(idx),
            DisplayText: displayText,
            Score: Number.isFinite(score) ? score : 0,
        }
    })
}
